import React, { useCallback, useEffect, useState } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { useMutation, useApolloClient } from '@apollo/client';

import { parseQueryStrings } from '@crehana/utils';
import type { LiveSubscriptionConfirmationDialogProps } from '@crehana/web/dist/components/LiveSubscriptionConfirmationDialog/types';
import LiveSubscriptionConfirmationDialog from '@crehana/web/dist/components/LiveSubscriptionConfirmationDialog';
import { MainFooter } from '@crehana/web';
import { useMe } from '@crehana/auth';

import MainMenu, { MainMenuWrapperProps } from 'Jsx/CrehanaWeb/MainMenuWrapper';
import type { InitialStateResult } from 'Jsx/Utils/getBaseInitialState';
import { usePageView } from 'Jsx/Analytics/UserActivity';

import Landing from './screens/Landing';
import B2BLanding from './screens/B2BLanding';
import Detail from './screens/Detail';
import B2BDetail from './screens/B2BDetail';
import Chat from './screens/Chat';

import ENROLL_USER_IN_LIVE from './graphql/EnrollUserInLive.graphql';
import type {
  EnrollUserInLive,
  EnrollUserInLiveVariables,
} from './graphql/types/EnrollUserInLive';
import GET_CURRENT_AND_NEXT_LIVES from './graphql/GetCurrentAndNextLives.graphql';
import type {
  GetCurrentAndNextLives,
  GetCurrentAndNextLives_currentLive_edges as Edges,
} from './graphql/types/GetCurrentAndNextLives';
import GET_LIVE_DETAIL from './graphql/GetLiveDetail.graphql';
import type {
  GetLiveDetail,
  GetLiveDetailVariables,
} from './graphql/types/GetLiveDetail';

import { CREHANA_LIVE_AMPLITUDE_PAGE_NAME } from './constants';
import type { LiveCategories, OrganizationType } from './types';

export type ExtraState = {
  liveCategories: LiveCategories;
};

export type AppProps = {
  LiveLandingV2: {
    organization: OrganizationType;
  };
};

const ENROLLMENT_QUERY_PARAM = 'enrollLive';

export type AppComplexProps = InitialStateResult<AppProps & ExtraState>;

const App: React.FC<AppComplexProps> = ({
  countryPrefix,
  liveCategories,
  LiveLandingV2,
}) => {
  usePageView({ type: 'live' });
  const { me } = useMe();
  const { location } = useHistory();
  // We use this state to hide the MainMenu in the mobile apps
  const [showMainMenu, setShowMainMenu] = useState(true);
  const [mainMenuLayoutProps, setMainMenuLayoutProps] = useState<
    Partial<MainMenuWrapperProps['layoutConfig']>
  >({
    hideTopBanner: true,
    hidePremiumLandingLink: true,
    hideShoppingCart: true,
    hideUserDashboardLink: true,
  });

  const addMainMenuLayoutProps = useCallback(
    (
      extraMainMenuLayoutProps: Partial<MainMenuWrapperProps['layoutConfig']>,
    ) => {
      setMainMenuLayoutProps(prevState => ({
        ...prevState,
        ...extraMainMenuLayoutProps,
      }));
    },
    [],
  );

  const [dialogState, setDialogState] = useState<
    {
      isOpen: boolean;
      live?: {
        originalId: number | null;
        calendarLink: string | null;
      } | null;
    } & Pick<LiveSubscriptionConfirmationDialogProps, 'subscriptionMutation'>
  >({
    isOpen: false,
    live: null,
    subscriptionMutation: { status: 'idle', errorMessage: undefined },
  });
  const isLiveLandingUrl =
    location.pathname === '/live' || location.pathname === '/live/';
  const position = isLiveLandingUrl ? 'absolute' : 'relative';
  const [enrollToLiveMutation] =
    useMutation<EnrollUserInLive, EnrollUserInLiveVariables>(
      ENROLL_USER_IN_LIVE,
    );
  const client = useApolloClient();

  useEffect(() => {
    const params = parseQueryStrings(window.location.search);

    if (
      params?.referer &&
      (params.referer === 'app-ios' || params.referer === 'app-android')
    ) {
      setShowMainMenu(false);
    }
  }, []);

  const makeEnrollment = useCallback(
    (liveId: number) => {
      setDialogState(prevDialogState => ({
        ...prevDialogState,
        live: { originalId: liveId, calendarLink: null },
        subscriptionMutation: { status: 'loading', errorMessage: undefined },
      }));

      return enrollToLiveMutation({ variables: { liveId } })
        .then(({ data }) => {
          if (data?.liveSubscription?.success) {
            const [, , liveSlug] = location.pathname.split('/');
            let calendarUrl: string | null = null;

            // If we are on live detail
            if (liveSlug) {
              const prevLiveDetail = client.readQuery<
                GetLiveDetail,
                GetLiveDetailVariables
              >({
                query: GET_LIVE_DETAIL,
                variables: { slug: liveSlug },
              });

              if (prevLiveDetail?.live) {
                client.writeQuery<GetLiveDetail, GetLiveDetailVariables>({
                  query: GET_LIVE_DETAIL,
                  variables: { slug: liveSlug },
                  data: {
                    ...prevLiveDetail,
                    live: {
                      ...prevLiveDetail.live,
                      isSubscribed: true,
                    },
                  },
                });
                calendarUrl = prevLiveDetail.live.calendarLink;
              }
            } else {
              const prevCurrAndNext = client.readQuery<GetCurrentAndNextLives>({
                query: GET_CURRENT_AND_NEXT_LIVES,
              });

              if (prevCurrAndNext?.nextLives) {
                const liveToUpdate = prevCurrAndNext.nextLives.edges.find(
                  e => e!.node!.originalId === liveId,
                );

                if (liveToUpdate) {
                  const updatedNextLives: (Edges | null)[] =
                    prevCurrAndNext.nextLives.edges.map(edge => {
                      if (edge!.node!.originalId === liveId) {
                        return {
                          ...edge,
                          node: {
                            ...edge!.node!,
                            isSubscribed: true,
                            __typename: 'LiveNode',
                          },
                          __typename: 'LiveNodeEdge',
                        };
                      }
                      return edge;
                    });

                  client.writeQuery<GetCurrentAndNextLives>({
                    query: GET_CURRENT_AND_NEXT_LIVES,
                    data: {
                      ...prevCurrAndNext,
                      nextLives: {
                        ...prevCurrAndNext.nextLives,
                        edges: updatedNextLives,
                      },
                    },
                  });
                  calendarUrl = liveToUpdate.node?.calendarLink || null;
                }
              }
            }

            setDialogState({
              isOpen: true,
              live: { originalId: liveId, calendarLink: calendarUrl },
              subscriptionMutation: {
                status: 'success',
                errorMessage: undefined,
              },
            });
          } else {
            setDialogState(prevDialogState => ({
              ...prevDialogState,
              isOpen: true,
              subscriptionMutation: {
                status: 'error',
                errorMessage: data?.liveSubscription?.error || '',
              },
            }));
          }
        })
        .catch(() =>
          setDialogState(prevDialogState => ({
            ...prevDialogState,
            isOpen: true,
            subscriptionMutation: { status: 'error' },
          })),
        );
    },
    [client, enrollToLiveMutation, location.pathname],
  );

  const enrollToLive = (liveId: number) => {
    if (!me) {
      const [, , liveSlug] = location.pathname.split('/');
      const param = `${ENROLLMENT_QUERY_PARAM}=${liveId}`;
      const queryParams = location.search
        ? `${location.search}&${param}`
        : `?${param}`;

      let nextUrl = `${location.pathname}${queryParams}`;

      if (!liveSlug) {
        const prevCurrAndNext = client.readQuery<GetCurrentAndNextLives>({
          query: GET_CURRENT_AND_NEXT_LIVES,
        });
        const selectedLive = prevCurrAndNext?.nextLives?.edges.find(
          e => e!.node!.originalId === liveId,
        );

        if (selectedLive && selectedLive.node?.slug) {
          nextUrl = `${location.pathname}${selectedLive.node.slug}${queryParams}`;
        }
      }
      window.showRegisterModal(
        nextUrl,
        undefined,
        undefined,
        'Lives - Enroll in live',
      );
      return Promise.resolve();
    }
    return makeEnrollment(liveId);
  };

  useEffect(() => {
    const params = parseQueryStrings(window.location.search);

    if (params && params[ENROLLMENT_QUERY_PARAM] && me) {
      makeEnrollment(Number(params[ENROLLMENT_QUERY_PARAM]));
    }
  }, [location.pathname, makeEnrollment, me]);

  return (
    <div className="min-h-screen bg-base-dark">
      <div className="w-full">
        <Switch>
          <Route exact path="/live/:liveSlug/chat/">
            <Chat />
          </Route>

          <Route path="/live/">
            {showMainMenu ? (
              <MainMenu
                position={position}
                isTransparent
                theme="dark"
                logoType="live"
                layoutConfig={mainMenuLayoutProps}
                customUrls={!isLiveLandingUrl ? { home: '/live/' } : undefined}
                amplitudePageName={CREHANA_LIVE_AMPLITUDE_PAGE_NAME}
              />
            ) : null}

            <Route exact path="/live/">
              <div className="sm:pt-72">
                <Landing
                  liveCategories={liveCategories}
                  setMainMenuProps={addMainMenuLayoutProps}
                  enrollToLive={enrollToLive}
                />
                <div className="relative zIndex-10">
                  <MainFooter isDark />
                </div>
              </div>
            </Route>

            <Route exact path="/live/:liveSlug/">
              <Detail
                setMainMenuProps={addMainMenuLayoutProps}
                enrollToLive={enrollToLive}
                isMobileApp={!showMainMenu}
                countryPrefix={countryPrefix}
              />
            </Route>
          </Route>

          <Route path="/org/:orgSlug/live/">
            {showMainMenu ? (
              <div className="bg-primary-main">
                <MainMenu
                  position={position}
                  isTransparent
                  theme="dark"
                  logoType="b2b"
                  layoutConfig={mainMenuLayoutProps}
                  amplitudePageName={CREHANA_LIVE_AMPLITUDE_PAGE_NAME}
                />
              </div>
            ) : null}

            <Route exact path="/org/:orgSlug/live/">
              <B2BLanding organization={LiveLandingV2.organization} />
            </Route>

            <Route exact path="/org/:orgSlug/live/:liveSlug/">
              <B2BDetail />
            </Route>
          </Route>
        </Switch>
      </div>

      {dialogState.live && (
        <LiveSubscriptionConfirmationDialog
          isOpen={dialogState.isOpen}
          onDismiss={() => {
            setDialogState({
              isOpen: false,
              live: null,
              subscriptionMutation: { status: 'idle', errorMessage: undefined },
            });
          }}
          live={dialogState.live}
          subscriptionMutation={dialogState.subscriptionMutation}
          onRetry={() => {
            if (dialogState.live?.originalId) {
              enrollToLive(dialogState.live.originalId);
            }
          }}
          isDark
        />
      )}
    </div>
  );
};

export default App;
